import React from "react";
import { Exercise, PaginationInfo, WorkoutDetails } from "../../api/types.ts";
import { WorkoutCard } from "./WorkoutCard.tsx";
import { useDialog } from "../DialogManager.tsx";

interface WorkoutListProps {
  workouts?: WorkoutDetails[];
  exercises?: Exercise[];
  timeFormat: "12h" | "24h";
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
}

const WorkoutList: React.FC<WorkoutListProps> = ({
  workouts,
  exercises,
  timeFormat,
  pagination,
  onPageChange,
}) => {
  const { openDialog } = useDialog();

  // Create a map for quick lookup
  const exercisesMap = React.useMemo(() => {
    const exerciseMap = new Map<string, Exercise>();
    exercises?.forEach((ex) => exerciseMap.set(ex.id, ex));
    return exerciseMap;
  }, [exercises]);

  return (
    <div className="flex flex-col gap-6 py-4">
      {!workouts || workouts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">
            There are no recorded workouts within this time range. Record one to
            get started!
          </p>
          <button
            onClick={() =>
              openDialog("record-workout", { exercises: exercises })
            }
            className="px-4 py-2 bg-primary text-white rounded hover:bg-opacity-90 transition-colors"
          >
            Get started
          </button>
        </div>
      ) : (
        <>
          {workouts.map((workout) => (
            <WorkoutCard
              key={workout.id}
              workout={workout}
              exercises={exercisesMap}
              timeFormat={timeFormat}
            />
          ))}
          {pagination && onPageChange && pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4 pb-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={!pagination.has_prev}
                className="px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 px-2">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
                className="px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { WorkoutList };
